import { useState } from 'react';
import { TENANT_VIEWS } from '../../constants/tenant';
import translations from '../../translations';
import type { TenantResponseAPI } from '../../types/Tenant';
import { FeatureFlag } from '../../utils/featureFlags';
import { useFeatureFlag } from '../featureFlags/FeatureFlagProvider';
import SearchInput from '../generic/SearchInput';
import Table, { type TableColumnList } from '../generic/Table';
import TenantName from '../generic/TenantName';

interface SubtenantListProps {
    navigateTo: (view: keyof typeof TENANT_VIEWS) => void;
    subtenants: TenantResponseAPI[];
}

const subtenantColumns: TableColumnList<TenantResponseAPI> = [
    {
        renderHeader: () => translations.COMMON_TABLE_name,
        renderCell: ({ item }) => <TenantName name={item?.developerName ?? ''} />,
    },
    {
        renderHeader: () => translations.COMMON_TABLE_summary,
        renderCell: ({ item }) => <div>{item.developerSummary}</div>,
    },
];

const SubtenantList = ({ navigateTo, subtenants }: SubtenantListProps) => {
    const [query, setQuery] = useState('');
    const [paging, setPaging] = useState({
        page: 1,
        pageSize: 20,
        total: subtenants?.length || 0,
    });

    const onSearch = (query: string) => {
        setQuery(query);
    };

    const onPage = (pageNumber: number) => {
        setPaging({
            ...paging,
            page: pageNumber,
        });
    };

    const pageStart = paging.pageSize * (paging.page - 1);
    const pageEnd = pageStart + paging.pageSize;

    const queriedTenants = (subtenants || []).filter(
        ({ developerName }) => !query || developerName?.toLowerCase().includes(query.toLowerCase()),
    );

    const visibleTenants = queriedTenants.slice(pageStart, pageEnd);

    const { getFlag } = useFeatureFlag();

    return (
        <>
            <div className="flex">
                {getFlag(FeatureFlag.SUBTNT) ? null : (
                    <button
                        onClick={() => navigateTo(TENANT_VIEWS.newSubtenant)}
                        className="btn btn-sm btn-success"
                        type="button"
                    >
                        <span className="glyphicon glyphicon-plus" />
                        {' New Subtenant'}
                    </button>
                )}
                <SearchInput
                    value={query}
                    onChange={onSearch}
                    className={!getFlag(FeatureFlag.SUBTNT) ? 'margin-left' : ''}
                />
            </div>
            <Table
                wrapperClassName="margin-top"
                columns={subtenantColumns}
                items={visibleTenants}
                pagination={{
                    page: paging.page,
                    total: queriedTenants.length,
                    pageSize: paging.pageSize,
                    changePage: onPage,
                }}
            />
        </>
    );
};

export default SubtenantList;
